import type { MarkdownBlockNode, MarkdownInlineNode, MarkdownListItemNode, MarkdownSection } from "./types";

/**
 * Content-aware section layouts for structured themes.
 *
 * Themes may present a section as a set of entries (works, products, cases) or as a
 * timeline (experience with years) when the author's Markdown already has that shape.
 * Detection is conservative: anything ambiguous stays prose, and every piece of author
 * text is preserved in the returned structure.
 */

export type ThemeEntry = {
  title: MarkdownInlineNode[];
  href?: string;
  description: MarkdownInlineNode[];
  extra: MarkdownBlockNode[];
};

export type ThemeTimelineItem = {
  period: string;
  body: MarkdownInlineNode[];
  extra: MarkdownBlockNode[];
};

export type ThemeSectionLayout =
  | { type: "entries"; lead: MarkdownBlockNode[]; entries: ThemeEntry[]; tail: MarkdownBlockNode[] }
  | { type: "timeline"; lead: MarkdownBlockNode[]; items: ThemeTimelineItem[] }
  | { type: "prose"; nodes: MarkdownBlockNode[] };

type ParagraphNode = Extract<MarkdownBlockNode, { type: "paragraph" }>;
type ListNode = Extract<MarkdownBlockNode, { type: "list" }>;

const ENTRY_SEPARATOR_RE = /^\s*(?:[:：|｜·—–]|-\s)\s*/;
const YEAR = "(?:19|20)\\d{2}";
const MONTH = "(?:\\s*[./年-]\\s*\\d{1,2}\\s*月?)?";
const OPEN_END = "(?:今|现在|至今|目前|now|present|today|heute|aujourd'hui|hoy|hoje|現在|今日|현재)";
const RANGE_SEPARATOR = "(?:—|–|-|~|～|〜|至|到|to|bis|à|a)";
const YEAR_SUFFIX = "(?:年起|年至今|年以来|年から|年より|年まで|年|起|以来|년부터|년까지|년)";
/** Leading year or range: "2020—2023 年", "2024年から", "2019–2021", "2022 – present". */
const PERIOD_RE = new RegExp(
  `^\\s*(${YEAR}${MONTH}(?:\\s*${RANGE_SEPARATOR}\\s*(?:${YEAR}${MONTH}|${OPEN_END}))?(?:\\s*${YEAR_SUFFIX})?)(?=$|[\\s:：,，、は.。)）]|[^\\d])\\s*[:：,，、は]?\\s*`,
  "i"
);
/** Leading "since" phrase: "Since 2024", "Desde 2024", "Seit 2024", "С 2024 года", "منذ 2024". */
const SINCE_PREFIX_RE = new RegExp(
  `^\\s*((?:since|from|desde|depuis|dès|seit|ab|a partir de|à partir de|с|от|منذ)\\s+${YEAR}(?:\\s*(?:года|г\\.))?)(?=$|[\\s:：,，、.])\\s*[:：,，、]?\\s*`,
  "i"
);
/** "Independent since 2024, …" — the phrase labels the item while the sentence stays intact. */
const SINCE_INLINE_RE = new RegExp(`^[^\\d.,:;。，：；]{1,32}?(?:^|\\s)((?:since|seit|depuis|desde)\\s+${YEAR})(?=$|[\\s,.;:])`, "i");

export function analyzeThemeSection(section: MarkdownSection): ThemeSectionLayout {
  return analyzeThemeNodes(section.nodes);
}

export function analyzeThemeNodes(nodes: MarkdownBlockNode[]): ThemeSectionLayout {
  const entries = detectEntries(nodes);
  if (entries) return entries;
  const timeline = detectTimeline(nodes);
  if (timeline) return timeline;
  return { type: "prose", nodes };
}

function detectEntries(nodes: MarkdownBlockNode[]): ThemeSectionLayout | null {
  const listIndexes = nodes.flatMap((node, index) => (node.type === "list" ? [index] : []));
  if (listIndexes.length !== 1) return null;
  const listIndex = listIndexes[0];
  const list = nodes[listIndex] as ListNode;
  const lead = nodes.slice(0, listIndex);
  const tail = nodes.slice(listIndex + 1);
  if (![...lead, ...tail].every((node) => node.type === "paragraph" || node.type === "blockquote")) return null;
  if (list.items.length === 0 || list.items.some((item) => item.checked !== undefined)) return null;

  const entries: ThemeEntry[] = [];
  for (const item of list.items) {
    const entry = toEntry(item);
    if (!entry) return null;
    entries.push(entry);
  }
  return { type: "entries", lead, entries, tail };
}

function toEntry(item: MarkdownListItemNode): ThemeEntry | null {
  const [first, ...extra] = item.children;
  if (!first || first.type !== "paragraph") return null;
  const inline = trimLeadingWhitespace(first.children);
  const head = inline[0];
  if (!head) return null;

  if (head.type === "link") {
    return {
      title: head.children,
      href: head.href,
      description: stripLeading(inline.slice(1), ENTRY_SEPARATOR_RE),
      extra
    };
  }

  if (head.type === "strong") {
    const onlyLink = head.children.length === 1 && head.children[0].type === "link" ? head.children[0] : null;
    return {
      title: onlyLink ? onlyLink.children : head.children,
      href: onlyLink?.href,
      description: stripLeading(inline.slice(1), ENTRY_SEPARATOR_RE),
      extra
    };
  }

  return null;
}

function detectTimeline(nodes: MarkdownBlockNode[]): ThemeSectionLayout | null {
  const candidates = collectTimelineCandidates(nodes);
  if (!candidates || candidates.items.length < 2) return null;

  const items: ThemeTimelineItem[] = [];
  let matched = 0;
  for (const candidate of candidates.items) {
    const period = matchPeriod(candidate.paragraph.children);
    if (period) {
      matched += 1;
      items.push({ period: period.period, body: period.rest, extra: candidate.extra });
    } else {
      items.push({ period: "", body: candidate.paragraph.children, extra: candidate.extra });
    }
  }

  if (matched < 2 || matched * 2 < items.length) return null;
  if (!items[0].period) return null;
  return { type: "timeline", lead: candidates.lead, items };
}

function collectTimelineCandidates(nodes: MarkdownBlockNode[]): {
  lead: MarkdownBlockNode[];
  items: Array<{ paragraph: ParagraphNode; extra: MarkdownBlockNode[] }>;
} | null {
  if (nodes.length > 0 && nodes.every((node) => node.type === "paragraph")) {
    const paragraphs = nodes as ParagraphNode[];
    const firstPeriodIndex = paragraphs.findIndex((paragraph) => matchPeriod(paragraph.children));
    if (firstPeriodIndex < 0 || firstPeriodIndex > 1) return null;
    return {
      lead: paragraphs.slice(0, firstPeriodIndex),
      items: paragraphs.slice(firstPeriodIndex).map((paragraph) => ({ paragraph, extra: [] }))
    };
  }

  const listIndexes = nodes.flatMap((node, index) => (node.type === "list" ? [index] : []));
  if (listIndexes.length !== 1) return null;
  const listIndex = listIndexes[0];
  const lead = nodes.slice(0, listIndex);
  if (listIndex !== nodes.length - 1 || !lead.every((node) => node.type === "paragraph")) return null;
  const list = nodes[listIndex] as ListNode;
  if (list.items.some((item) => item.checked !== undefined)) return null;

  const items: Array<{ paragraph: ParagraphNode; extra: MarkdownBlockNode[] }> = [];
  for (const item of list.items) {
    const [first, ...extra] = item.children;
    if (!first || first.type !== "paragraph") return null;
    items.push({ paragraph: first, extra });
  }
  return { lead, items };
}

function matchPeriod(inline: MarkdownInlineNode[]): { period: string; rest: MarkdownInlineNode[] } | null {
  const trimmed = trimLeadingWhitespace(inline);
  const head = trimmed[0];
  if (!head) return null;

  if (head.type === "text") {
    const match = PERIOD_RE.exec(head.value) ?? SINCE_PREFIX_RE.exec(head.value);
    if (match) {
      const rest = head.value.slice(match[0].length);
      if (!rest.trim() && trimmed.length === 1) return null;
      return {
        period: match[1].trim(),
        rest: rest ? [{ type: "text", value: rest }, ...trimmed.slice(1)] : trimLeadingWhitespace(trimmed.slice(1))
      };
    }
    const inline = SINCE_INLINE_RE.exec(head.value);
    if (inline) return { period: inline[1].trim(), rest: trimmed };
    return null;
  }

  if (head.type === "strong") {
    const label = getInlineText(head.children).trim();
    const match = PERIOD_RE.exec(label);
    if (!match || match[0].trim().length < label.length - 1) return null;
    const rest = stripLeading(trimmed.slice(1), /^\s*[:：,，、]?\s*/);
    if (rest.length === 0) return null;
    return { period: match[1].trim(), rest };
  }

  return null;
}

function trimLeadingWhitespace(inline: MarkdownInlineNode[]): MarkdownInlineNode[] {
  const [head, ...rest] = inline;
  if (!head || head.type !== "text") return inline;
  const value = head.value.replace(/^\s+/, "");
  return value ? [{ type: "text", value }, ...rest] : trimLeadingWhitespace(rest);
}

function stripLeading(inline: MarkdownInlineNode[], pattern: RegExp): MarkdownInlineNode[] {
  const [head, ...rest] = inline;
  if (!head || head.type !== "text") return inline;
  const value = head.value.replace(pattern, "");
  return value ? [{ type: "text", value }, ...rest] : trimLeadingWhitespace(rest);
}

export function getInlineText(nodes: MarkdownInlineNode[]): string {
  return nodes.map((node) => {
    switch (node.type) {
      case "text":
      case "inlineCode":
      case "source":
        return node.value;
      case "break":
        return " ";
      case "strong":
      case "emphasis":
      case "delete":
      case "link":
        return getInlineText(node.children);
    }
  }).join("");
}

export function hashThemeText(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}
